import React from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { FCard } from "../../../components/card";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, } from "chart.js";
import { Line } from "react-chartjs-2";
import { between, colCenter } from "@ui/components/style/style";
import { Icon } from "@iconify/react";
// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);
export const AlarmsChart = ({ data, title = "Alarms", }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    // Prepare data for Chart.js using time as labels
    const chartData = {
        labels: data.map((item) => item.time),
        datasets: [
            {
                label: "Alarms",
                data: data.map((item) => item.count),
                borderColor: "#3B3561",
                backgroundColor: "transparent",
                borderWidth: 3,
                pointRadius: 4,
                pointBackgroundColor: "#3B3561",
                pointBorderColor: "#FFFFFF",
                pointBorderWidth: 2,
                pointHoverRadius: 8,
                pointHoverBackgroundColor: "#3B3561",
                pointHoverBorderColor: "#FFFFFF",
                pointHoverBorderWidth: 3,
                tension: 0.4,
                fill: false,
            },
        ],
    };
    // Calculate max value for Y axis
    const maxCount = Math.max(...data.map(d => d.count), 10);
    const yMax = Math.ceil(maxCount / 10) * 10 || 100;
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: true,
                backgroundColor: "rgba(59, 53, 97, 0.95)",
                titleColor: "#FFFFFF",
                titleFont: {
                    size: 14,
                    weight: 'bold',
                },
                bodyColor: "#FFFFFF",
                bodyFont: {
                    size: 13,
                },
                borderColor: "#3B3561",
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                displayColors: false,
                callbacks: {
                    title: function (context) {
                        var _a;
                        return ((_a = context[0]) === null || _a === void 0 ? void 0 : _a.label) || '';
                    },
                    label: function (context) {
                        const index = context.dataIndex;
                        const dataPoint = data[index];
                        if (!dataPoint)
                            return;
                        const lines = [];
                        lines.push(`Total Alarms: ${dataPoint.count.toLocaleString()}`);
                        lines.push(`✓ Acknowledged: ${dataPoint.acknowledgedCount.toLocaleString()}`);
                        lines.push(`✗ Unacknowledged: ${dataPoint.unacknowledgedCount.toLocaleString()}`);
                        // Calculate percentage
                        if (dataPoint.count > 0) {
                            const ackPercent = ((dataPoint.acknowledgedCount / dataPoint.count) * 100).toFixed(1);
                            lines.push(`Acknowledgment Rate: ${ackPercent}%`);
                        }
                        return lines;
                    },
                },
            },
        },
        scales: {
            x: {
                display: true,
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                ticks: {
                    color: theme.palette.text.secondary,
                    font: {
                        size: isMobile ? 11 : 12,
                    },
                    maxRotation: 0,
                    padding: isMobile ? 5 : 10,
                },
            },
            y: {
                display: true,
                min: 0,
                max: yMax,
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                ticks: {
                    color: theme.palette.text.secondary,
                    font: {
                        size: isMobile ? 11 : 12,
                    },
                    padding: isMobile ? 5 : 10,
                    stepSize: Math.ceil(yMax / 4),
                },
            },
        },
        layout: {
            padding: {
                left: isMobile ? 5 : 10,
                right: isMobile ? 5 : 10,
                top: isMobile ? 5 : 10,
                bottom: isMobile ? 5 : 10,
            },
        },
    };
    return (React.createElement(FCard, null,
        React.createElement(Box, { sx: { p: { xs: 1, sm: 2.5 } } },
            React.createElement(Box, { sx: Object.assign(Object.assign({}, between), { mb: { xs: 2, sm: 3 } }) },
                React.createElement(Box, { sx: Object.assign(Object.assign({}, colCenter), { gap: 1 }) },
                    React.createElement(Icon, { icon: "hugeicons:chart-line-data-03", width: isMobile ? 20 : 28, height: isMobile ? 20 : 28 }),
                    React.createElement(Typography, { variant: isMobile ? "subtitle1" : "h6", color: "text.primary" }, title))),
            React.createElement(Box, { sx: {
                    width: "100%",
                    height: { xs: 250, sm: 270 },
                    position: "relative",
                } },
                React.createElement(Line, { data: chartData, options: options })))));
};
